"use client";
import { PinIcon } from "@soma/ui";
import { dashboardContent } from "@/lib/content";
import { useMyHotkey } from "@/lib/hooks/useMyHotkey";
import { cn } from "@/lib/utils";

/**
 * Marks a hotkey as the miner this browser follows, or clears the mark. `boxed` matches the
 * copy button beside a hotkey on the miner page; `bare` sits in a leaderboard row, where a
 * border on every line would turn the hotkey column into a column of buttons - it shows on
 * hover and stays visible only on the row that is marked.
 */
export function MineButton({
  hotkey,
  variant = "boxed",
  className,
}: {
  hotkey: string;
  variant?: "boxed" | "bare";
  className?: string;
}) {
  const t = dashboardContent.myMiner;
  const mine = useMyHotkey();
  const marked = mine.hotkey === hotkey;
  const label = marked ? t.unmark : t.mark;

  return (
    <button
      type="button"
      onClick={() => mine.toggle(hotkey)}
      aria-pressed={marked}
      aria-label={label}
      title={label}
      className={cn(
        "inline-flex shrink-0 cursor-pointer items-center justify-center transition-[color,opacity,border-color]",
        variant === "boxed"
          ? cn(
              "size-7 rounded-md border",
              marked ? "border-accent-line text-accent" : "border-line text-fg-muted hover:border-fg-muted hover:text-fg",
            )
          : cn(
              "size-6 rounded-md focus-visible:opacity-100",
              marked ? "text-accent" : "text-fg-faint opacity-0 group-hover/row:opacity-100 hover:text-accent",
            ),
        className,
      )}
    >
      <PinIcon filled={marked} />
    </button>
  );
}
